import React, { useMemo } from "react";
import useDashboardData from "../../../hooks/useDashboardData";
import KpiTile from "../../../components/dashboard/KpiTile";
import {
  sumKey,
  sumKeys,
  trendForKeys,
} from "../../../utils/chart/mapDashboardSeries";

const computeDelta = (current, previous) => {
  if (!previous) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
};

const splitForDelta = (rows, keys) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { current: 0, previous: 0, trend: [] };
  }
  const trend = trendForKeys(rows, keys);
  const half = Math.floor(trend.length / 2);
  const previous = trend.slice(0, half).reduce((a, b) => a + b, 0);
  const current = trend.slice(half).reduce((a, b) => a + b, 0);
  return { current: sumKeys(rows, keys), previous, trend };
};

const KpiSlot = ({ label, type, keys, accent, filters }) => {
  const { data, loading } = useDashboardData(type, filters);
  const { current, previous, trend } = useMemo(
    () => splitForDelta(data, keys),
    [data, keys]
  );
  const delta = useMemo(
    () => computeDelta(current, previous),
    [current, previous]
  );

  return (
    <KpiTile
      label={label}
      value={current}
      delta={trend.length > 1 ? delta : null}
      deltaNote={trend.length > 1 ? "vs. prior period" : ""}
      trend={trend}
      accent={accent}
      loading={loading}
    />
  );
};

const DashboardKpis = ({ decode, filters }) => {
  const role = decode.role;

  const showApplications =
    !(role === "manager" && !decode.applicationPrivilege);
  const showMaintenance =
    !(role === "manager" && !decode.applicationPrivilege) ||
    !(role === "manager" && !decode.ticketPrivilege);
  const showProperties = role === "company" || role === "manager";
  const showAppointments = !(
    (role === "manager" && !decode.calendarPrivilege) ||
    role === "admin"
  );

  const slots = [];

  if (showApplications) {
    slots.push({
      key: "applications",
      label: "Applications",
      type: "applicant",
      keys: ["pending", "approved", "denied"],
      accent: "var(--rdc-brand-500)",
    });
  }
  if (showMaintenance) {
    slots.push({
      key: "maintenance",
      label: "Open Maintenance",
      type: "ticket2",
      keys: ["open", "inprogress"],
      accent: "var(--rdc-warning)",
    });
  }
  if (showProperties) {
    slots.push({
      key: "properties",
      label: "Active Properties",
      type: "property",
      keys: ["active"],
      accent: "var(--rdc-success)",
    });
  }
  if (showAppointments) {
    slots.push({
      key: "appointments",
      label: "Appointments Booked",
      type: "user_appointment",
      keys: ["booked"],
      accent: "var(--rdc-info)",
    });
  }

  if (slots.length === 0) return null;

  return (
    <div className="rdc-kpi-strip">
      {slots.map((s) => (
        <KpiSlot
          key={s.key}
          label={s.label}
          type={s.type}
          keys={s.keys}
          accent={s.accent}
          filters={filters}
        />
      ))}
    </div>
  );
};

export default DashboardKpis;
